import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User } from './AuthContext'

// Material categories - only plastic counts toward plastic credits
export type MaterialCategory = 'plastic' | 'aluminium' | 'paper' | 'glass'

export interface SubmissionMaterial {
  id: string
  category: MaterialCategory
  name: string
  volumeKg: number // kilograms
}

export type SubmissionStatus = 'draft' | 'pending' | 'processing' | 'verified' | 'rejected'

export interface RecyclingSubmission {
  id: string
  supplierId: string
  supplierName: string
  reportType: string
  collectionDate: string // ISO date string
  district: string
  materials: SubmissionMaterial[]
  notes?: string
  status: SubmissionStatus
  createdAt: string
  blockchainTx?: string
  verifiedAt?: string
}

interface DistrictTotals {
  district: string
  plasticKg: number
  aluminiumKg: number
  paperKg: number
  glassKg: number
  credits: number // == plasticKg (1kg plastic = 1 credit)
}

// Credit events allow tracking issuance (upon verification) and retirement (admin action)
export type CreditEventType = 'issued' | 'retired'
export interface CreditEvent {
  id: string
  submissionId?: string
  supplierId: string
  supplierName: string
  type: CreditEventType
  credits: number
  timestamp: string
  note?: string
}

export interface NotificationItem {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  message: string
  createdAt: string
  read?: boolean
}

interface RecyclingContextType {
  submissions: RecyclingSubmission[]
  addSubmission: (data: Omit<RecyclingSubmission, 'id' | 'status' | 'createdAt' | 'blockchainTx' | 'verifiedAt'>) => RecyclingSubmission
  verifySubmission: (id: string) => void
  getSupplierTotals: (supplierId: string) => { plasticKg: number; totalKg: number; credits: number }
  getAllTotals: () => { plasticKg: number; totalKg: number; credits: number }
  getDistrictTotals: () => DistrictTotals[]
  topDistricts: (limit?: number) => DistrictTotals[]
  getSupplierSubmissions: (supplierId: string) => RecyclingSubmission[]
  creditEvents: CreditEvent[]
  retireCredits: (supplierId: string, amount: number, note?: string) => boolean
  getSupplierCreditBalance: (supplierId: string) => number
  listSuppliers: () => Array<{ supplierId: string; supplierName: string; credits: number; plasticKg: number; submissions: number }>
  notifications: NotificationItem[]
  markNotificationRead: (id: string) => void
  addNotification: (n: Omit<NotificationItem, 'id' | 'createdAt' | 'read'>) => void
}

const RecyclingContext = createContext<RecyclingContextType | undefined>(undefined)

const LS_KEY = 'recycling_submissions'
const LS_EVENTS_KEY = 'recycling_credit_events'
const LS_NOTIF_KEY = 'recycling_notifications'

// Minimal helper to generate pseudo tx hash
const randomHash = () => '0x' + Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('')

// Seed mock data (including non-plastic materials) if none present
const seedData = (): RecyclingSubmission[] => {
  const today = new Date().toISOString().slice(0, 10)
  return [
    {
      id: 'SUB-SEED-001',
      supplierId: '3',
      supplierName: 'OneChain Corporate Client',
      reportType: 'monthly',
      collectionDate: today,
      district: 'Sha Tin',
      materials: [
        { id: 'm1', category: 'plastic', name: 'Plastic Bottles (PET)', volumeKg: 850 },
        { id: 'm2', category: 'aluminium', name: 'Aluminium Cans', volumeKg: 120 },
        { id: 'm3', category: 'paper', name: 'Paper Drink Cartons', volumeKg: 75 }
      ],
      notes: 'Seed monthly report',
      status: 'verified',
      createdAt: new Date().toISOString(),
      blockchainTx: randomHash(),
      verifiedAt: new Date().toISOString()
    },
    {
      id: 'SUB-SEED-002',
      supplierId: '3',
      supplierName: 'OneChain Corporate Client',
      reportType: 'weekly',
      collectionDate: today,
      district: 'Kowloon City',
      materials: [
        { id: 'm4', category: 'plastic', name: 'Plastic Bottles (PET)', volumeKg: 320 },
        { id: 'm5', category: 'glass', name: 'Glass Bottles', volumeKg: 40 }
      ],
      notes: 'Seed weekly report',
      status: 'processing',
      createdAt: new Date().toISOString()
    }
  ]
}

export const RecyclingProvider: React.FC<{ children: ReactNode; currentUser?: User | null }> = ({ children }) => {
  const [submissions, setSubmissions] = useState<RecyclingSubmission[]>([])
  const [creditEvents, setCreditEvents] = useState<CreditEvent[]>([])
  const [notifications, setNotifications] = useState<NotificationItem[]>([])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY)
      if (raw) {
        setSubmissions(JSON.parse(raw))
      } else {
        const seeded = seedData()
        setSubmissions(seeded)
        localStorage.setItem(LS_KEY, JSON.stringify(seeded))
      }
      const evRaw = localStorage.getItem(LS_EVENTS_KEY)
      if (evRaw) setCreditEvents(JSON.parse(evRaw))
      const notifRaw = localStorage.getItem(LS_NOTIF_KEY)
      if (notifRaw) setNotifications(JSON.parse(notifRaw))
    } catch (e) {
      console.error('Failed to load recycling submissions', e)
    }
  }, [])

  const persist = (data: RecyclingSubmission[]) => {
    setSubmissions(data)
    try { localStorage.setItem(LS_KEY, JSON.stringify(data)) } catch {}
  }

  const persistEvents = (events: CreditEvent[]) => {
    setCreditEvents(events)
    try { localStorage.setItem(LS_EVENTS_KEY, JSON.stringify(events)) } catch {}
  }

  const persistNotifications = (items: NotificationItem[]) => {
    setNotifications(items)
    try { localStorage.setItem(LS_NOTIF_KEY, JSON.stringify(items)) } catch {}
  }

  const addSubmission: RecyclingContextType['addSubmission'] = (data) => {
    const newSubmission: RecyclingSubmission = {
      id: 'SUB-' + Date.now(),
      status: 'pending',
      createdAt: new Date().toISOString(),
      ...data
    }
    const updated = [newSubmission, ...submissions]
    persist(updated)
    return newSubmission
  }

  const verifySubmission = (id: string) => {
    const updated: RecyclingSubmission[] = submissions.map(s => {
      if (s.id === id) {
        return {
          ...s,
            status: 'verified',
            blockchainTx: s.blockchainTx || randomHash(),
            verifiedAt: new Date().toISOString()
        }
      }
      return s
    })
    persist(updated)
    // Issue credits if not already issued for this submission
    const submission = updated.find(s => s.id === id)
    if (submission) {
      const plastic = submission.materials.filter(m=>m.category==='plastic').reduce((a,m)=>a+m.volumeKg,0)
      if (plastic > 0 && !creditEvents.some(ev => ev.submissionId === submission.id && ev.type === 'issued')) {
        const ev: CreditEvent = {
          id: 'CE-' + Date.now(),
          submissionId: submission.id,
          supplierId: submission.supplierId,
          supplierName: submission.supplierName,
          type: 'issued',
          credits: plastic,
          timestamp: new Date().toISOString(),
          note: 'Automatic issuance on verification'
        }
        const newEvents = [ev, ...creditEvents]
        persistEvents(newEvents)
        addNotification({ type: 'success', message: `Issued ${plastic} credits for ${submission.id}` })
      }
    }
  }

  const aggregate = (list: RecyclingSubmission[]) => {
    const plasticKg = list.reduce((sum, s) => sum + s.materials.filter(m => m.category === 'plastic').reduce((a, m) => a + m.volumeKg, 0), 0)
    const totalKg = list.reduce((sum, s) => sum + s.materials.reduce((a, m) => a + m.volumeKg, 0), 0)
    return { plasticKg, totalKg, credits: plasticKg }
  }

  const getSupplierTotals = (supplierId: string) => aggregate(submissions.filter(s => s.supplierId === supplierId))
  const getAllTotals = () => aggregate(submissions)

  const getDistrictTotals = () => {
    const map: Record<string, DistrictTotals> = {}
    submissions.forEach(s => {
      if (!map[s.district]) {
        map[s.district] = { district: s.district, plasticKg: 0, aluminiumKg: 0, paperKg: 0, glassKg: 0, credits: 0 }
      }
      s.materials.forEach(m => {
        switch (m.category) {
          case 'plastic':
            map[s.district].plasticKg += m.volumeKg
            map[s.district].credits += m.volumeKg
            break
          case 'aluminium': map[s.district].aluminiumKg += m.volumeKg; break
          case 'paper': map[s.district].paperKg += m.volumeKg; break
          case 'glass': map[s.district].glassKg += m.volumeKg; break
        }
      })
    })
    return Object.values(map).sort((a, b) => b.plasticKg - a.plasticKg)
  }

  const topDistricts = (limit = 3) => getDistrictTotals().slice(0, limit)

  const getSupplierSubmissions = (supplierId: string) => submissions.filter(s => s.supplierId === supplierId)

  const getSupplierCreditBalance = (supplierId: string) => {
    return creditEvents.filter(e=>e.supplierId===supplierId).reduce((acc,e)=> acc + (e.type==='issued'? e.credits : -e.credits),0)
  }

  const retireCredits: RecyclingContextType['retireCredits'] = (supplierId, amount, note) => {
    const balance = getSupplierCreditBalance(supplierId)
    if (amount <= 0 || amount > balance) return false
    const supplierName = submissions.find(s=>s.supplierId===supplierId)?.supplierName || 'Unknown Supplier'
    const ev: CreditEvent = {
      id: 'CE-' + Date.now(),
      supplierId,
      supplierName,
      type: 'retired',
      credits: amount,
      timestamp: new Date().toISOString(),
      note: note || 'Retirement'
    }
    persistEvents([ev, ...creditEvents])
    addNotification({ type: 'info', message: `Retired ${amount} credits for supplier ${supplierName}` })
    return true
  }

  const listSuppliers = () => {
    const map: Record<string, { supplierId: string; supplierName: string; credits: number; plasticKg: number; submissions: number }> = {}
    submissions.forEach(s => {
      if (!map[s.supplierId]) {
        map[s.supplierId] = { supplierId: s.supplierId, supplierName: s.supplierName, credits: 0, plasticKg: 0, submissions: 0 }
      }
      map[s.supplierId].submissions += 1
      map[s.supplierId].plasticKg += s.materials.filter(m=>m.category==='plastic').reduce((a,m)=>a+m.volumeKg,0)
    })
    Object.values(map).forEach(entry => {
      entry.credits = getSupplierCreditBalance(entry.supplierId)
    })
    return Object.values(map).sort((a,b)=> b.credits - a.credits)
  }

  const addNotification: RecyclingContextType['addNotification'] = (n) => {
    const item: NotificationItem = { id: 'NT-' + Date.now(), createdAt: new Date().toISOString(), read: false, ...n }
    const updated = [item, ...notifications].slice(0, 200)
    persistNotifications(updated)
  }

  const markNotificationRead = (id: string) => {
    persistNotifications(notifications.map(n=> n.id===id ? { ...n, read: true } : n))
  }

  return (
    <RecyclingContext.Provider value={{ submissions, addSubmission, verifySubmission, getSupplierTotals, getAllTotals, getDistrictTotals, topDistricts, getSupplierSubmissions, creditEvents, retireCredits, getSupplierCreditBalance, listSuppliers, notifications, markNotificationRead, addNotification }}>
      {children}
    </RecyclingContext.Provider>
  )
}

export const useRecycling = () => {
  const ctx = useContext(RecyclingContext)
  if (!ctx) throw new Error('useRecycling must be used within RecyclingProvider')
  return ctx
}
